import React, { useState } from 'react';
import { ApiError, apiFetch } from '../../lib/api';
import { formatMaxDisplay, formatRuleText, isParseError, parseFormatInput, summaryFormat } from '../../lib/shooting';
import { type TournamentDetail, type TournamentSummary } from '../../hooks/useScoringTournament';
import { FormatFields, type FormatDraft } from './FormatFields';
import { scrollPinFieldIntoView } from '../common/pin-dialog-frame';
import { wholeNumberScoring } from '../../lib/round1';

interface FormatCardProps {
  tournament: TournamentSummary;
  onSaved: (result: { tournament: TournamentSummary; leaderboard: TournamentDetail['leaderboard'] }) => void;
}

/** Shows the tournament's format; Edit changes it after the server checks the PIN. */
export function FormatCard({ tournament, onSaved }: FormatCardProps) {
  const format = summaryFormat(tournament);
  const maxDisplay = (value: typeof format) => wholeNumberScoring(tournament) ? String(value.countedShots * 10) : formatMaxDisplay(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FormatDraft>({ shots: '', counted: '' });
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setDraft({
      shots: String(format.shotsPerShooter),
      counted: format.countedShots === format.shotsPerShooter ? '' : String(format.countedShots),
    });
    setPin('');
    setError(null);
    setEditing(true);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const parsed = parseFormatInput({ shotsPerShooter: draft.shots, countedShots: draft.counted });
    if (isParseError(parsed)) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = await apiFetch<{ tournament: TournamentSummary; leaderboard: TournamentDetail['leaderboard'] }>(
        `/api/admin/shooting/tournaments/${tournament.id}/format`,
        { method: 'PATCH', body: JSON.stringify({ ...parsed.format, pin }) },
      );
      onSaved(body);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the format.');
      // Keep the PIN when it was right (e.g. shots too low); clear it after a wrong PIN or lockout.
      if (!(err instanceof ApiError) || err.status === 403 || err.status === 429) setPin('');
    } finally {
      setBusy(false);
    }
  }

  const preview = parseFormatInput({ shotsPerShooter: draft.shots, countedShots: draft.counted });
  return (
    <section className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-[var(--color-text-secondary)]">Format</h2>
          <p className="text-base font-semibold text-[var(--color-text-primary)]">
            {formatRuleText(format)} · max {maxDisplay(format)}
          </p>
        </div>
        {editing ? null : (
          <button
            type="button"
            onClick={startEdit}
            className="min-h-11 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] px-4 text-sm font-medium text-[var(--color-text-primary)]"
          >
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <form onSubmit={(event) => void save(event)} className="mt-4 space-y-4">
          <FormatFields value={draft} onChange={setDraft} />
          <p className="text-sm text-[var(--color-text-secondary)]">
            {isParseError(preview) ? preview.error : `${formatRuleText(preview.format)} · max ${maxDisplay(preview.format)}`}
          </p>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">PIN</span>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              maxLength={12}
              aria-label="PIN"
              value={pin}
              onFocus={(event) => scrollPinFieldIntoView(event.currentTarget)}
              onChange={(event) => {
                setPin(event.target.value.replace(/\D/g, ''));
                setError(null);
              }}
              className="h-12 w-full rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] px-3 text-center text-xl tracking-[0.4em]"
            />
          </label>
          {error ? (
            <p className="text-sm font-medium text-[var(--color-error-text)]" role="alert">
              {error}
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="min-h-12 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !pin || !preview.ok}
              className="min-h-12 rounded-[var(--radius-md)] bg-[var(--color-accent)] text-sm font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save format'}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
